import React from 'react';
import hoistNonReactStatic from 'hoist-non-react-statics';
import location from 'services/router/location';
import language from 'services/i18n/language';
import ff, { type Flags } from 'services/feature-toggle';
import settings from 'configurations/application';
import user from 'store/user';
import env from 'utils/environment';
import is from 'utils/is';
import type { PartialToNullable } from 'contracts';

interface PortalRedirectionOptions {
  /**
   * Goes directly to the destination url without hitting "profile/select" endpoint.
   */
  bypass?: boolean | (() => boolean);
  /**
   * Custom feature flag. Redirects to portal IF the given feature flag is disabled.
   */
  featureFlag?: keyof typeof Flags;
  /**
   * Forces "enabledQAMode" feature flag to be handled as it disabled.
   */
  ignoreQAModeFlag?: boolean;
  /**
   * Overwrites destination pathname.
   */
  overwriteDestinationPathname?: () => string;
  onBeforeRedirect?: () => void;
}

const getDisplayName = function DisplayName<T>(ReactComponent: React.FunctionComponent<T>): string {
  return ReactComponent.displayName || ReactComponent.name || 'Component';
};

const defaultOptions: PartialToNullable<PortalRedirectionOptions> = {
  bypass: false,
  featureFlag: undefined,
  ignoreQAModeFlag: undefined,
  overwriteDestinationPathname: undefined,
  onBeforeRedirect: undefined,
};

const mountRedirectionUrl = (pathname: string, options = defaultOptions): string => {
  const { href, origin, pathname: originPathname, hash } = window.location;
  const targetOrigin = env.isDevelopment
    ? origin.replace(settings.mscPortal.redirection.dev.match, settings.mscPortal.redirection.dev.replace)
    : origin.replace(
        settings.mscPortal.redirection.production.match,
        settings.mscPortal.redirection.production.replace
      );

  // Combine origin and destination query params
  const searchParams = new URLSearchParams([
    ...new URL(href).searchParams,
    ...new URLSearchParams(new URL(pathname, origin).search),
  ]).toString();

  let target = new URL(href).pathname
    .replace(origin, '')
    .replace(originPathname, options.overwriteDestinationPathname?.() || new URL(pathname, targetOrigin).pathname);

  if (searchParams) {
    target = `${target}?${searchParams}`;
  }

  if (hash) target += hash;

  const path: string[] = ['/'];

  const bypass = is.func(options.bypass) ? options.bypass() : options.bypass;

  if (bypass) {
    path.push(target);
  } else {
    const key = user.isStudioView() ? 'studio' : 'performer';
    const id = user.data.viewTypeId!;

    path.push(language.current, `profile/select?profileKey=${key}_${id}&redirectUrl=${encodeURIComponent(target)}`);
  }

  return `${targetOrigin}${path.join('/').replace(/\/{2,}/g, '/')}`;
};

const shouldRedirectToPortal = (host: string, options: PartialToNullable<PortalRedirectionOptions>): boolean => {
  const isBranchDeploymentURI = settings.mscPortal.branchDeployment.test(host);

  // always redirect to portal in production environment
  if (env.isProduction) return true;

  // check if current app environment is eligible for redirection
  const isEnvEligibleForRedirection = env.isStaging || (env.isDevelopment && !isBranchDeploymentURI);

  // flags false if enableQAMode is active AND env is not eligible for redirection
  if (!isEnvEligibleForRedirection || (!options.ignoreQAModeFlag && ff.enabled('enableQAMode'))) {
    return false;
  }

  //  flags true if custom feature flag is active
  return is.nullish(options.featureFlag) || ff.disabled(options.featureFlag);
};

const withPortalRedirection = function Redirect<T>(
  Component: React.FunctionComponent<T>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  LoadingComponent: React.FunctionComponent<any>,
  options: PortalRedirectionOptions = defaultOptions
): React.FunctionComponent<T> {
  const Redirection: React.FunctionComponent<T> = (props: T) => {
    const { origin, pathname } = window.location;

    const mergedOptions = { ...defaultOptions, ...options };

    const shouldRedirect = shouldRedirectToPortal(origin, mergedOptions);

    if (shouldRedirect) {
      mergedOptions?.onBeforeRedirect?.();

      location.navigate(mountRedirectionUrl(pathname, mergedOptions), '_self', true);

      return <LoadingComponent />;
    }

    // @ts-expect-error: type inference
    return <Component {...props} />;
  };

  Redirection.displayName = `withPortalRedirection(${getDisplayName(Component)})`;

  hoistNonReactStatic(Redirection, Component);

  return Redirection;
};

export type { PortalRedirectionOptions };
export { mountRedirectionUrl, defaultOptions };
export default withPortalRedirection;
